import { FC, memo, useEffect, useState, useCallback, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useForm, SubmitHandler, UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { APP_ROUTES, AUTH_ROUTES } from '@navigation/types';
import theme from 'theme';
import { authSlice } from '@redux/state/authSlice';
import { useAppDispatch } from '@redux/store';
import { useSetPasswordMutation } from '@redux/queries/auth';
import { useAuthUser, useForgotPasswordEmail } from '@redux/selectors/auth';
import { password } from '@utils/constants/schemas';
import {
  TEXT_INPUT_PLACEHOLDERS,
  TEXT_INPUT_LABELS,
  TEXT_INPUT_ICONS,
} from '@utils/constants/form';
import { getRedirectPath, clearRedirectPath } from '@utils/redirect';
import AuthLayout from '@components/layouts/AuthLayout';
import AuthTitle from '@components/auth/AuthTitle';
import TextInput from '@components/fields/input';
import PasswordInput from '@components/fields/input/PasswordInput';
import {
  StyledButton,
  StyledForm,
  StyledErrorContainer,
  StyledErrorText,
} from '@components/form/styled';

const Content = styled.div`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 24px;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type SetPasswordState = {
  password: string;
  code: string;
};

const defaultValues: SetPasswordState = {
  password: '',
  code: '',
};

const schema = yup.object().shape({
  password,
  code: yup.string().required('Code is required'),
});

const SetPassword: FC = () => {
  const [setPassword, { isSuccess, isLoading, isError }] =
    useSetPasswordMutation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user: authUser } = useAuthUser();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const email = useForgotPasswordEmail();
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    reset: resetForm,
    watch,
    formState: { errors },
  } = useForm<SetPasswordState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const handleChange = useCallback(
    ({
      field,
      text,
      onChange,
    }: {
      field: keyof SetPasswordState;
      text: string;
      onChange: UseFormSetValue<SetPasswordState>;
    }) => {
      clearErrors(field);
      onChange(field, text);

      if (errorMessage) {
        setErrorMessage('');
      }
    },
    [clearErrors, errorMessage, setErrorMessage],
  );

  const onSubmit: SubmitHandler<SetPasswordState> = (
    data: SetPasswordState,
  ) => {
    if (data && email) {
      setPassword({ ...data, email });
    }

    resetForm({}, { keepValues: true, keepDirty: false });
  };

  useEffect(() => {
    if (isSuccess) {
      const from =
        (location.state as { from?: string })?.from || getRedirectPath();

      dispatch(authSlice.actions.setForgotPasswordEmail(null));
      if (authUser && !authUser?.username) {
        navigate(AUTH_ROUTES.ONBOARDING, { replace: true });
      } else {
        clearRedirectPath();
        navigate(from || APP_ROUTES.HOME, { replace: true });
      }
    }
  }, [isSuccess, navigate, authUser, dispatch, location.state]);

  useEffect(() => {
    if (isError) {
      setErrorMessage('Invalid data');
    }
  }, [isError]);

  return (
    <AuthLayout>
      <Content>
        <AuthTitle
          title='Set new password'
          subtitle={
            "If you can't see the email, check other places it might be, like your junk, spam, social and other folders."
          }
        />
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <PasswordInput
              placeholder={TEXT_INPUT_PLACEHOLDERS.PASSWORD}
              label={TEXT_INPUT_LABELS.PASSWORD}
              errorText={errors.password?.message}
              {...register('password')}
              value={watch('password')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange({
                  field: 'password',
                  text: e.target.value,
                  onChange: setValue,
                })
              }
              disabled={isLoading}
            />

            <TextInput
              placeholder={TEXT_INPUT_PLACEHOLDERS.VERIFICATION_CODE}
              label={TEXT_INPUT_LABELS.VERIFICATION_CODE}
              icon={TEXT_INPUT_ICONS.VERIFICATION_CODE}
              errorText={errors.code?.message}
              {...register('code')}
              value={watch('code')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange({
                  field: 'code',
                  text: e.target.value,
                  onChange: setValue,
                })
              }
              disabled={isLoading}
            />

            <StyledErrorContainer>
              {errorMessage && (
                <StyledErrorText>{errorMessage}</StyledErrorText>
              )}
            </StyledErrorContainer>

            <StyledButton
              disabled={isLoading || !watch('password') || !watch('code')}
              isLoading={isLoading}
              type='submit'
              textStyle={{ fontSize: theme.fonts.fontSize.xl }}
            >
              Set new password
            </StyledButton>
          </FormContainer>
        </StyledForm>
      </Content>
    </AuthLayout>
  );
};

export default memo(SetPassword);
